import * as bootstrap from 'bootstrap';
import htmx from 'htmx.org';

import { initializePasswordToggles } from './components/password-toggle.js';
import { initializeManagementContext } from './components/management-context.js';
import { initializePluginComponents } from './plugins.js';

window.bootstrap = bootstrap;
window.htmx = htmx;

const getCsrfToken = () => {
    return document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? '';
};

const normalizeFormAccessibility = (root = document) => {
    root.querySelectorAll('.form-error, .invalid-feedback, [data-error-message]')
        .forEach((message, index) => {
            if (!(message instanceof HTMLElement)) {
                return;
            }

            if (!message.id) {
                message.id = `form-error-${Date.now()}-${index}`;
            }

            const container = message.closest(
                '.input, .form-group, .mb-3, .form-floating, [data-form-control]'
            );
            const control = container?.querySelector(
                'input:not([type="hidden"]), select, textarea'
            );

            if (!(control instanceof HTMLElement)) {
                return;
            }

            control.setAttribute('aria-invalid', 'true');

            const describedBy = new Set(
                (control.getAttribute('aria-describedby') ?? '')
                    .split(/\\s+/)
                    .filter(Boolean)
            );
            describedBy.add(message.id);
            control.setAttribute(
                'aria-describedby',
                Array.from(describedBy).join(' ')
            );
        });
};

const focusFirstPageMessageOrHeading = (root = document) => {
    const error = root.querySelector(
        '#flash-messages .alert-danger, #flash-messages .message.error, ' +
        '.form-error, .invalid-feedback'
    );

    if (error instanceof HTMLElement) {
        const control = error.closest(
            '.input, .form-group, .mb-3, .form-floating, [data-form-control]'
        )?.querySelector('input:not([type="hidden"]), select, textarea');

        if (control instanceof HTMLElement) {
            control.focus({ preventScroll: true });
            control.scrollIntoView({ block: 'center', behavior: 'auto' });
            return;
        }
    }

    const pageContent = root.id === 'page-content'
        ? root
        : root.querySelector('#page-content');

    if (pageContent instanceof HTMLElement) {
        pageContent.focus({ preventScroll: true });
    }
};

const tickerStateStorageKey = 'kupana-management-ticker-state';

const hashTickerText = (value) => {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(16);
};

const initializeManagementNewsTicker = (root = document) => {
    root.querySelectorAll('[data-management-news-ticker]').forEach((ticker) => {
        if (!(ticker instanceof HTMLElement) || ticker.dataset.tickerReady === '1') {
            return;
        }

        const track = ticker.querySelector('[data-management-news-ticker-track]');
        const sequence = ticker.querySelector('[data-management-news-ticker-sequence]');

        if (!(track instanceof HTMLElement) || !(sequence instanceof HTMLElement)) {
            return;
        }

        ticker.dataset.tickerReady = '1';

        const sequenceWidth = sequence.scrollWidth;
        if (sequenceWidth <= 0) {
            return;
        }

        const duration = Math.max(
            22000,
            Math.min(180000, Math.round((sequenceWidth / 52) * 1000))
        );
        const signature = hashTickerText(sequence.textContent?.trim() ?? '');
        const now = Date.now();
        let state = null;

        try {
            state = JSON.parse(sessionStorage.getItem(tickerStateStorageKey) ?? 'null');
        } catch {
            state = null;
        }

        if (
            !state
            || state.signature !== signature
            || !Number.isFinite(Number(state.startedAt))
            || Number(state.startedAt) > now
        ) {
            state = { signature, startedAt: now };
        }

        const persistState = () => {
            try {
                sessionStorage.setItem(tickerStateStorageKey, JSON.stringify(state));
            } catch {
                // SessionStorage ist optional; der Ticker funktioniert auch ohne Persistenz.
            }
        };

        const elapsed = (now - Number(state.startedAt)) % duration;
        track.style.setProperty('--management-ticker-duration', `${duration}ms`);
        track.style.setProperty('--management-ticker-delay', `-${elapsed}ms`);
        persistState();

        let pauseStartedAt = null;

        const pause = () => {
            if (pauseStartedAt !== null) {
                return;
            }

            pauseStartedAt = Date.now();
            track.style.animationPlayState = 'paused';
        };

        const resume = () => {
            if (pauseStartedAt === null) {
                return;
            }

            state.startedAt = Number(state.startedAt) + (Date.now() - pauseStartedAt);
            pauseStartedAt = null;
            track.style.animationPlayState = 'running';
            persistState();
        };

        ticker.addEventListener('mouseenter', pause);
        ticker.addEventListener('mouseleave', resume);
        ticker.addEventListener('focusin', pause);
        ticker.addEventListener('focusout', resume);

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                pause();
            } else {
                resume();
            }
        });
    });
};

const initializeManagementNotificationMenus = (root = document) => {
    root.querySelectorAll('[data-management-notification-menu]').forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement) || wrapper.dataset.notificationReady === '1') {
            return;
        }

        const toggle = wrapper.querySelector('[data-notification-toggle]');
        const list = wrapper.querySelector('[data-notification-list]');
        const badge = wrapper.querySelector('[data-notification-badge]');
        const count = wrapper.querySelector('[data-notification-count]');
        const autoload = wrapper.querySelector('[data-notification-autoload]');
        const markAllRead = wrapper.querySelector('[data-notification-mark-all-read]');
        const previewUrl = wrapper.dataset.previewUrl ?? '';
        const markAllReadUrl = wrapper.dataset.markAllReadUrl ?? '';
        const centerUrl = wrapper.dataset.centerUrl ?? '/management/notifications';

        if (!(toggle instanceof HTMLElement) || !(list instanceof HTMLElement) || previewUrl === '') {
            return;
        }

        wrapper.dataset.notificationReady = '1';
        let nextOffset = 0;
        let hasMore = true;
        let loading = false;
        let initialized = false;

        const updateUnreadCount = (value) => {
            const unreadCount = Math.max(0, Number(value) || 0);

            if (count instanceof HTMLElement) {
                count.textContent = String(unreadCount);
            }

            if (badge instanceof HTMLElement) {
                badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
                badge.classList.toggle('d-none', unreadCount === 0);
            }

            if (markAllRead instanceof HTMLElement) {
                markAllRead.classList.toggle('d-none', unreadCount === 0);
            }

            toggle.setAttribute(
                'aria-label',
                unreadCount === 1
                    ? '1 ungelesene Benachrichtigung'
                    : `${unreadCount} ungelesene Benachrichtigungen`
            );
        };

        const renderEmpty = () => {
            list.replaceChildren();
            const empty = document.createElement('div');
            empty.className = 'p-4 text-center text-body-secondary';
            empty.innerHTML = '<i class="bi bi-bell-slash fs-3 d-block mb-2" aria-hidden="true"></i>';
            empty.append(document.createTextNode('Keine neuen Benachrichtigungen'));
            list.append(empty);
        };

        const renderError = () => {
            if (nextOffset > 0) {
                return;
            }

            list.replaceChildren();
            const error = document.createElement('div');
            error.className = 'p-4 text-center text-danger small';
            error.textContent = 'Benachrichtigungen konnten nicht geladen werden.';
            list.append(error);
        };

        const appendNotification = (notification) => {
            const link = document.createElement('a');
            link.href = typeof notification.targetUrl === 'string' && notification.targetUrl.startsWith('/')
                ? notification.targetUrl
                : centerUrl;
            link.className = 'list-group-item list-group-item-action py-3';

            const header = document.createElement('div');
            header.className = 'd-flex justify-content-between gap-3 mb-1';

            const title = document.createElement('strong');
            title.className = 'small';
            title.textContent = notification.title ?? '';

            const created = document.createElement('span');
            created.className = 'small text-body-secondary text-nowrap';
            created.textContent = notification.createdLabel ?? '';

            const message = document.createElement('div');
            message.className = 'small text-body-secondary management-notification-message';
            message.textContent = notification.message ?? '';

            header.append(title, created);
            link.append(header, message);
            list.append(link);
        };

        const updateAutoload = (visible = false) => {
            if (!(autoload instanceof HTMLElement)) {
                return;
            }

            autoload.classList.toggle('d-none', !visible);
            autoload.setAttribute('aria-hidden', visible ? 'false' : 'true');
        };

        const loadNotifications = async (reset = false) => {
            if (loading || (!reset && !hasMore)) {
                return;
            }

            loading = true;
            updateAutoload(!reset && nextOffset > 0);
            if (reset) {
                nextOffset = 0;
                hasMore = true;
            }

            try {
                const url = new URL(previewUrl, window.location.origin);
                url.searchParams.set('offset', String(nextOffset));
                url.searchParams.set('limit', '5');

                const response = await fetch(url, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error(`Notification preview failed: ${response.status}`);
                }

                const payload = await response.json();
                const notifications = Array.isArray(payload.notifications)
                    ? payload.notifications
                    : [];

                if (reset) {
                    list.replaceChildren();
                }

                notifications.forEach(appendNotification);
                nextOffset = Number(payload.nextOffset) || (nextOffset + notifications.length);
                hasMore = Boolean(payload.hasMore);
                updateUnreadCount(payload.unreadCount);

                if (nextOffset === 0 || (reset && notifications.length === 0)) {
                    renderEmpty();
                    hasMore = false;
                }

                updateAutoload(false);
            } catch (error) {
                console.error(error);
                renderError();
                hasMore = false;
                updateAutoload(false);
            } finally {
                loading = false;
                updateAutoload(false);

                if (hasMore && list.scrollHeight <= list.clientHeight + 2) {
                    window.requestAnimationFrame(maybeAutoload);
                }
            }
        };

        const maybeAutoload = () => {
            if (!hasMore || loading) {
                return;
            }

            const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
            if (remaining <= 72 || list.scrollHeight <= list.clientHeight + 2) {
                loadNotifications(false);
            }
        };

        wrapper.addEventListener('show.bs.dropdown', () => {
            if (!initialized) {
                initialized = true;
                loadNotifications(true).then(maybeAutoload);
            }
        });

        list.addEventListener('scroll', maybeAutoload, { passive: true });


        if (markAllRead instanceof HTMLButtonElement && markAllReadUrl !== '') {
            markAllRead.addEventListener('click', async () => {
                if (markAllRead.disabled) {
                    return;
                }

                markAllRead.disabled = true;

                try {
                    const response = await fetch(markAllReadUrl, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-Token': getCsrfToken(),
                        },
                        credentials: 'same-origin',
                    });

                    if (!response.ok) {
                        throw new Error(`Mark all read failed: ${response.status}`);
                    }

                    const payload = await response.json();
                    nextOffset = 0;
                    hasMore = false;
                    updateUnreadCount(payload.unreadCount ?? 0);
                    renderEmpty();
                    updateAutoload(false);
                } catch (error) {
                    console.error(error);
                    document.dispatchEvent(new CustomEvent('kupana:error', {
                        detail: { message: 'Benachrichtigungen konnten nicht als gelesen markiert werden.' },
                    }));
                } finally {
                    markAllRead.disabled = false;
                }
            });
        }
    });
};

const initializeBootstrapComponents = (root = document) => {
    root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
        bootstrap.Tooltip.getOrCreateInstance(element);
    });

    root.querySelectorAll('[data-bs-toggle="popover"]').forEach((element) => {
        bootstrap.Popover.getOrCreateInstance(element);
    });
};

const themeStorageKey = 'kupana-theme';

const getPreferredTheme = () => {
    const storedTheme = localStorage.getItem(themeStorageKey);

    if (storedTheme === 'light' || storedTheme === 'dark') {
        return storedTheme;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
};

const updateThemeIcon = (theme) => {
    document.querySelectorAll('[data-theme-icon]').forEach((icon) => {
        icon.className = theme === 'dark'
            ? 'bi bi-sun'
            : 'bi bi-moon-stars';
    });
};

const setTheme = (theme) => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem(themeStorageKey, theme);
    updateThemeIcon(theme);
};

const initializeTheme = () => {
    setTheme(getPreferredTheme());
};

const normalizeManagementPath = (pathname) => {
    const normalized = pathname.length > 1
        ? pathname.replace(/\/+$/, '')
        : pathname;

    if (/^\/management\/player\/[^/]+$/.test(normalized)) {
        return '/management/squad';
    }

    if (/^\/management\/matches\/[^/]+$/.test(normalized)) {
        return '/management/matches';
    }

    return normalized;
};

const synchronizeManagementNavigation = () => {
    const activePath = normalizeManagementPath(window.location.pathname);

    document.querySelectorAll('.management-nav-link').forEach((link) => {
        const href = link.getAttribute('href');

        if (!href || href === '#') {
            link.classList.remove('active');
            link.removeAttribute('aria-current');
            return;
        }

        const linkPath = normalizeManagementPath(
            new URL(href, window.location.origin).pathname
        );
        const isActive = linkPath === activePath;

        link.classList.toggle('active', isActive);

        if (isActive) {
            link.setAttribute('aria-current', 'page');
        } else {
            link.removeAttribute('aria-current');
        }
    });
};

const normalizeAdminPath = (pathname) => {
    return pathname.length > 1
        ? pathname.replace(/\/+$/, '')
        : pathname;
};

const synchronizeAdminNavigation = () => {
    const activePath = normalizeAdminPath(window.location.pathname);
    const links = Array.from(
        document.querySelectorAll('.admin-nav-link[href]')
    );

    const candidates = links
        .map((link) => {
            const href = link.getAttribute('href');

            if (!href || href === '#') {
                return null;
            }

            const linkPath = normalizeAdminPath(
                new URL(href, window.location.origin).pathname
            );

            if (
                activePath !== linkPath
                && !activePath.startsWith(`${linkPath}/`)
            ) {
                return null;
            }

            return { link, linkPath };
        })
        .filter(Boolean);

    const longestPathLength = candidates.reduce(
        (length, candidate) => Math.max(length, candidate.linkPath.length),
        0
    );

    links.forEach((link) => {
        const href = link.getAttribute('href');
        let isActive = false;

        if (href && href !== '#') {
            const linkPath = normalizeAdminPath(
                new URL(href, window.location.origin).pathname
            );

            isActive = linkPath.length === longestPathLength
                && (
                    activePath === linkPath
                    || activePath.startsWith(`${linkPath}/`)
                );
        }

        link.classList.toggle('active', isActive);

        if (isActive) {
            link.setAttribute('aria-current', 'page');
        } else {
            link.removeAttribute('aria-current');
        }
    });
};

const initializeApplication = (root = document) => {
    initializeTheme();
    initializeBootstrapComponents(root);
    initializePasswordToggles(root);
    initializeManagementContext(root);
    initializeManagementNewsTicker(root);
    initializeManagementNotificationMenus(root);
    initializePluginComponents(root);
    normalizeFormAccessibility(root);
    synchronizeManagementNavigation();
    synchronizeAdminNavigation();
};

const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
).matches;

window.scrollTo({
    top: 0,
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
});

document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const themeToggle = target?.closest('[data-theme-toggle]');

    if (themeToggle) {
        const currentTheme = document.documentElement
            .getAttribute('data-bs-theme');

        setTheme(currentTheme === 'dark' ? 'light' : 'dark');
        return;
    }

    const publicRowLink = target?.closest('[data-public-row-link]');

    if (publicRowLink && !target?.closest('a, button, input, select, textarea, label')) {
        const href = publicRowLink.getAttribute('data-public-row-link');

        if (href) {
            window.location.assign(href);
            return;
        }
    }

    const navigationLink = target?.closest(
        '.management-nav-link[href], .admin-nav-link[href]'
    );

    if (!navigationLink || navigationLink.getAttribute('href') === '#') {
        return;
    }

    const selector = navigationLink.classList.contains('admin-nav-link')
        ? '.admin-nav-link.active'
        : '.management-nav-link.active';

    document.querySelectorAll(selector).forEach((item) => {
        item.classList.remove('active');
        item.removeAttribute('aria-current');
    });

    navigationLink.classList.add('active');
    navigationLink.setAttribute('aria-current', 'page');

    const offcanvasElement = navigationLink.closest(
        '.management-offcanvas, .admin-offcanvas'
    );

    if (offcanvasElement) {
        bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement).hide();
    }
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
        return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const publicRowLink = target?.closest('[data-public-row-link]');

    if (!publicRowLink || target?.closest('a, button, input, select, textarea, label')) {
        return;
    }

    const href = publicRowLink.getAttribute('data-public-row-link');

    if (href) {
        event.preventDefault();
        window.location.assign(href);
    }
});

window.addEventListener('popstate', () => {
    synchronizeManagementNavigation();
    synchronizeAdminNavigation();
});

document.addEventListener('DOMContentLoaded', () => {
    initializeApplication(document);
});

document.body.addEventListener('htmx:configRequest', (event) => {
    const csrfToken = getCsrfToken();

    if (csrfToken) {
        event.detail.headers['X-CSRF-Token'] = csrfToken;
    }

    event.detail.headers['X-Requested-With'] = 'XMLHttpRequest';
});

document.body.addEventListener('htmx:beforeRequest', () => {
    document.body.classList.add('is-loading');
    document.querySelector('#page-content')?.setAttribute('aria-busy', 'true');
});

document.body.addEventListener('htmx:afterRequest', () => {
    document.body.classList.remove('is-loading');
    document.querySelector('#page-content')?.setAttribute('aria-busy', 'false');
});

document.body.addEventListener('htmx:beforeSwap', (event) => {
    const xhr = event.detail.xhr;
    const status = xhr.status;
    const responseUrl = xhr.responseURL
        ? new URL(xhr.responseURL, window.location.origin)
        : null;
    const isLoginResponse = responseUrl !== null && (
        responseUrl.pathname === '/anmelden'
        || responseUrl.pathname === '/gamemaster/anmelden'
    );

    if (isLoginResponse) {
        event.detail.shouldSwap = false;
        window.location.assign(responseUrl.href);
        return;
    }

    if (status === 401 || status === 403) {
        event.detail.shouldSwap = false;
        window.location.reload();
    }
});

document.body.addEventListener('htmx:afterSwap', (event) => {
    initializeApplication(event.detail.target);

    if (event.detail.target.id === 'page-content') {
        event.detail.target.setAttribute('aria-busy', 'false');
        focusFirstPageMessageOrHeading(event.detail.target);

        window.scrollTo({
            top: 0,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
    }
});

document.body.addEventListener('htmx:responseError', (event) => {
    console.error('HTMX request failed', {
        status: event.detail.xhr.status,
        response: event.detail.xhr.responseText,
    });

    document.dispatchEvent(new CustomEvent('kupana:error', {
        detail: {
            message: 'Die Anfrage konnte nicht verarbeitet werden.',
        },
    }));
});

document.addEventListener('kupana:error', (event) => {
    const toastElement = document.querySelector('#global-error-toast');

    if (!toastElement) {
        return;
    }

    const messageElement = toastElement.querySelector('[data-toast-message]');

    if (messageElement) {
        messageElement.textContent = event.detail.message;
    }

    bootstrap.Toast.getOrCreateInstance(toastElement).show();
});

/**
 * The management sidebar survives HTMX page swaps. Keep task badges in that
 * persistent shell synchronized with the counters embedded in each fresh
 * management page fragment.
 */
const synchronizeManagementTaskCounters = (root = document) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const payload = scope.querySelector('[data-management-task-counters]');

    if (!payload) {
        return;
    }

    let counters;
    try {
        counters = JSON.parse(payload.textContent || '{}');
    } catch (error) {
        console.error('Invalid management task counter payload.', error);
        return;
    }

    const update = (selector, values) => {
        document.querySelectorAll(selector).forEach((badge) => {
            const key = selector === '[data-task-counter-menu]'
                ? badge.getAttribute('data-task-counter-menu')
                : selector === '[data-task-counter-team]'
                    ? badge.getAttribute('data-task-counter-team')
                    : badge.getAttribute('data-task-counter-sport');
            const count = Math.max(0, Number(values?.[key] ?? 0) || 0);
            badge.textContent = String(count);
            badge.classList.toggle('d-none', count < 1);
        });
    };

    update('[data-task-counter-sport]', counters.bySport || {});
    update('[data-task-counter-team]', counters.byTeam || {});
    update('[data-task-counter-menu]', counters.activeMenu || {});
};

document.addEventListener('DOMContentLoaded', () => synchronizeManagementTaskCounters(document));
document.body.addEventListener('htmx:afterSwap', (event) => {
    if (event.detail.target?.id === 'page-content') {
        synchronizeManagementTaskCounters(event.detail.target);
    }
});
